import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Trophy, Copy, Users, Flame, Loader2 } from "lucide-react";

const INDICATOR_LABELS = {
  price: "Price",
  rsi: "RSI (14)",
  macd_hist: "MACD hist",
  ema_cross_20_50: "EMA 20 × 50 cross",
  volume: "Volume",
};

const OP_LABELS = {
  ">": ">", "<": "<", ">=": "≥", "<=": "≤",
  cross_up: "crosses ↑", cross_down: "crosses ↓",
};

export default function Leaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forkingIdx, setForkingIdx] = useState(-1);
  const [days, setDays] = useState(7);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get(`/alerts/leaderboard?days=${days}&limit=25`).then((r) => {
      setRows(r.data.rows || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const fork = async (row, idx) => {
    setForkingIdx(idx);
    try {
      await api.post("/alerts/rules/fork", {
        symbol: row.symbol,
        asset_type: row.asset_type,
        timeframe: row.timeframe,
        logic: row.logic,
        conditions: row.conditions,
      });
      toast.success("Rule forked to your alerts", {
        action: { label: "Open Alerts", onClick: () => navigate("/alerts") },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fork failed");
    } finally { setForkingIdx(-1); }
  };

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Community</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <Trophy className="w-7 h-7 text-[hsl(var(--ai))]" /> Alert Leaderboard
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Anonymous ranking of the alert rules firing most often across all Aanar users. Fork any setup to your own alerts with one click.
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    data-testid={`lb-days-${d}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      days === d ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"
                    }`}>{d}d</button>
          ))}
        </div>
      </div>

      <Card className="p-5 rounded-2xl" data-testid="lb-list">
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading rankings…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="lb-empty">
            No rules have fired in the last {days} days yet. Be the first — build one on the Alerts page.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="py-2 text-left font-medium">Rank</th>
                  <th className="py-2 text-left font-medium">Symbol</th>
                  <th className="py-2 text-left font-medium">Rule</th>
                  <th className="py-2 text-left font-medium">TF</th>
                  <th className="py-2 text-right font-medium">Fires</th>
                  <th className="py-2 text-right font-medium">Users</th>
                  <th className="py-2 text-left font-medium">Last</th>
                  <th className="py-2 text-right font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-border/60" data-testid={`lb-row-${i}`}>
                    <td className="py-2">
                      <span className={`font-mono-tab text-base ${i === 0 ? "text-[hsl(var(--ai))]" : "text-muted-foreground"}`}>#{i + 1}</span>
                    </td>
                    <td className="py-2 font-medium">
                      <span className="uppercase">{r.symbol}</span>
                      <span className="text-[10px] text-muted-foreground ml-1">{r.asset_type}</span>
                    </td>
                    <td className="py-2 text-muted-foreground max-w-md">
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                        {(r.conditions || []).map((c, j) => (
                          <span key={j} className="flex items-center gap-1">
                            {j > 0 && <span className="text-[10px] uppercase tracking-widest text-muted-foreground/60">{r.logic}</span>}
                            <span>{INDICATOR_LABELS[c.indicator] || c.indicator} {OP_LABELS[c.operator] || c.operator}{" "}
                              <span className="font-mono-tab text-foreground">{c.threshold}</span></span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2">{r.timeframe}</td>
                    <td className="py-2 text-right font-mono-tab">
                      <span className="inline-flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-500" />{r.fires}</span>
                    </td>
                    <td className="py-2 text-right font-mono-tab text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{r.users}</span>
                    </td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {r.last_fired_at ? new Date(r.last_fired_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => fork(r, i)} disabled={forkingIdx === i}
                              data-testid={`lb-fork-${i}`} className="rounded-full">
                        {forkingIdx === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
                        Fork
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!loading && rows.length > 0 && (
        <div className="text-xs text-center text-muted-foreground" data-testid="lb-privacy-note">
          All rules are shown anonymously. Aanar never exposes which user created a rule.
        </div>
      )}
    </div>
  );
}
